import { useEffect, useState, type ReactNode } from 'react';
import {
    ReportTemplatePageMarginUnit,
    ReportTemplatePageOrientation,
    ReportTemplatePageSize,
    SwarmType,
} from '@/graphql/types';
import { array, mixed, type AnySchema } from 'yup';
import classNames from '../../../../../utils/classNames';
import { BaseReportElement } from '../BaseReportElement';
import { usePreviewPageScale } from '../../hooks/usePreviewPageScale';
import { renderDefinitionListWithAddElementSlot } from '../renderHelpers';
import {
    REPORT_TEMPLATE_PAGE_MARGIN_UNIT_TRANSLATIONS,
    REPORT_TEMPLATE_PAGE_ORIENTATION_TRANSLATIONS,
    REPORT_TEMPLATE_PAGE_SIZE_TRANSLATIONS,
} from '../../enums';
import {
    DEFAULT_REPORT_TEMPLATE_PAGE_MARGIN,
    buildPageMarginValueValidationSchema,
    pageMarginToCss,
    parsePageMargin,
    type PageMargin,
} from '../../pageSettings';
import type {
    ContextForKind,
    ElementDefinition,
    ElementContentRenderResult,
    ElementSettings,
    ElementSettingsFormField,
    ElementSettingsFormSchema,
    RenderContextKind,
    RenderRuntime,
    ResolveElementSettingsFormSchemaArgs,
} from '../types';
import { formatSwarmType, type SupportedSwarmTypes } from '../../swarmCompatibility';
import { SwarmTypeBadges } from '../../SwarmTypeBadges';
import { FlowablePageBreakGuides, FlowablePageEditorNotice } from './FlowablePageEditorGuide';

const NO_MARGIN: PageMargin = Object.freeze({
    value: 0,
    unit: ReportTemplatePageMarginUnit.Cm,
});

// Built once: `settingsFormSchema` is a getter and the settings form memoizes the schema by deep
// comparison, so a fresh function per read would remount the form on every render.
const buildPageMarginValueSchema = buildPageMarginValueValidationSchema('unit');

export interface PageElementSettings extends ElementSettings {
    children: ElementDefinition[];
    supportedSwarmTypes: SupportedSwarmTypes;
    noMargins?: boolean;
    pageMargin?: PageMargin | null;
    pageSize?: ReportTemplatePageSize;
    pageOrientation?: ReportTemplatePageOrientation;
}

function resolvePageMargin(
    settings: PageElementSettings,
    inheritedPageMargin: PageMargin
): PageMargin {
    const { pageMargin } = settings;
    // Parsed above the branch, so a malformed override fails the same way whether or not
    // "no margins" makes it irrelevant.
    const overriddenPageMargin =
        pageMargin === null || typeof pageMargin === 'undefined'
            ? inheritedPageMargin
            : parsePageMargin(pageMargin, 'page.settings.pageMargin');

    return settings.noMargins === true ? NO_MARGIN : overriddenPageMargin;
}

interface PageFrameProps {
    pageWidth: string;
    pageHeight: string;
    pageMargin: string;
    profileClassName: string;
    renderSurface: RenderRuntime['renderSurface'];
    isFlowablePage: boolean;
    supportedSwarmTypes: SupportedSwarmTypes;
    children: ReactNode;
}

function PageContentGate({
    hasContent,
    children,
}: {
    hasContent: Promise<boolean>;
    children: ReactNode;
}): JSX.Element | null {
    const [isVisible, setIsVisible] = useState<boolean | null>(null);

    useEffect(() => {
        let isActive = true;
        setIsVisible(null);

        void hasContent.then(
            (nextIsVisible) => {
                if (isActive) {
                    setIsVisible(nextIsVisible);
                }
            },
            () => {
                // Readiness owns content-resolution failures. Handling the rejection here
                // prevents an unhandled derived promise while the template boundary
                // reports the single readiness failure.
            }
        );

        return () => {
            isActive = false;
        };
    }, [hasContent]);

    return isVisible ? <>{children}</> : null;
}

function buildSelectedSwarmTypesValidationSchema(): AnySchema {
    return array()
        .of(mixed<SwarmType>().oneOf(Object.values(SwarmType)).required())
        .min(1, gettext('SELECT_AT_LEAST_ONE_SENSOR_TYPE'))
        .nullable()
        .defined(gettext('SENSOR_TYPE_SELECTION_REQUIRED'))
        .test(
            'unique-supported-swarm-types',
            gettext('SELECT_EACH_SENSOR_TYPE_ONCE'),
            (value) => value === null || new Set(value).size === value.length
        );
}

function buildGenericSwarmTypesValidationSchema(): AnySchema {
    return mixed()
        .nullable()
        .defined(gettext('SENSOR_TYPE_SELECTION_REQUIRED'))
        .test(
            'generic-project-page',
            gettext('PROJECT_PAGE_ALL_SENSOR_TYPES_REQUIRED'),
            (value) => value === null
        );
}

type PageSettingsSchemaContext = RenderContextKind | 'context-agnostic';

function createSupportedSwarmTypesField(
    schemaContext: PageSettingsSchemaContext
): ElementSettingsFormField {
    const contextKind = schemaContext === 'context-agnostic' ? null : schemaContext;
    const isProjectContext = contextKind === 'project';

    return {
        name: 'supportedSwarmTypes',
        label: gettext('SUPPORTED_SENSOR_TYPES'),
        helpText: isProjectContext
            ? gettext('SENSOR_SPECIFIC_PAGE_REPEAT_REQUIRED')
            : gettext('SUPPORTED_SENSOR_TYPES_HELP_TEXT'),
        type: 'swarmTypes',
        required: false,
        readOnly: isProjectContext,
        options: Object.values(SwarmType).map((swarmType) => [
            swarmType,
            formatSwarmType(swarmType),
        ]),
        default: null,
        buildValidationSchema: isProjectContext
            ? buildGenericSwarmTypesValidationSchema
            : buildSelectedSwarmTypesValidationSchema,
    };
}

function createPageSettingsFormSchema(
    schemaContext: PageSettingsSchemaContext
): ElementSettingsFormSchema {
    return {
        fields: [
            {
                name: 'children',
                label: gettext('CHILDREN'),
                type: 'definitions',
                required: true,
                default: [],
            },
            createSupportedSwarmTypesField(schemaContext),
            {
                name: 'pageSize',
                label: gettext('PAGE_SIZE'),
                type: 'choice',
                required: false,
                get options() {
                    return Object.values(ReportTemplatePageSize).map((value): [string, string] => [
                        value,
                        REPORT_TEMPLATE_PAGE_SIZE_TRANSLATIONS[value],
                    ]);
                },
                default: null,
            },
            {
                name: 'pageOrientation',
                label: gettext('ORIENTATION'),
                type: 'choice',
                required: false,
                get options() {
                    return Object.values(ReportTemplatePageOrientation).map(
                        (value): [string, string] => [
                            value,
                            REPORT_TEMPLATE_PAGE_ORIENTATION_TRANSLATIONS[value],
                        ]
                    );
                },
                default: null,
            },
            {
                name: 'noMargins',
                label: gettext('NO_MARGINS'),
                type: 'boolean',
                default: false,
            },
            {
                name: 'pageMargin',
                label: gettext('MARGIN'),
                type: 'group',
                required: false,
                // Clearing the value drops the override, which means "inherit the template".
                saveAsNullWhenMissingFields: ['value'],
                default: null,
                fields: [
                    {
                        name: 'value',
                        label: gettext('VALUE'),
                        type: 'float',
                        required: false,
                        buildValidationSchema: buildPageMarginValueSchema,
                        default: null,
                    },
                    {
                        name: 'unit',
                        label: gettext('MARGIN_UNIT'),
                        type: 'choice',
                        // The group itself is optional; a margin that exists needs its unit.
                        required: true,
                        validateOptions: true,
                        get options() {
                            return Object.values(ReportTemplatePageMarginUnit).map(
                                (value): [string, string] => [
                                    value,
                                    REPORT_TEMPLATE_PAGE_MARGIN_UNIT_TRANSLATIONS[value],
                                ]
                            );
                        },
                        default: DEFAULT_REPORT_TEMPLATE_PAGE_MARGIN.unit,
                    },
                ],
            },
        ],
    };
}

function PageFrame({
    pageWidth,
    pageHeight,
    pageMargin,
    profileClassName,
    renderSurface,
    isFlowablePage,
    supportedSwarmTypes,
    children,
}: PageFrameProps): JSX.Element {
    const { containerRef, pageRef, wrapperStyle, transformStyle } = usePreviewPageScale();
    const pageClassName = classNames(
        'report-page relative shrink-0 bg-white box-border',
        profileClassName,
        renderSurface !== 'export' && 'border border-gray-200 shadow-md'
    );
    const isExport = renderSurface === 'export';
    const showFlowableEditorGuide = renderSurface === 'editor' && isFlowablePage;
    const padding = isExport ? '0' : pageMargin;
    const width = isExport ? '100%' : pageWidth;
    const height = isExport ? '100%' : pageHeight;
    // Gallery chunks need a definite printable height even when this logical page
    // grows across multiple sheets. A percentage height cannot resolve from minHeight.
    const printableHeight = `calc(${pageHeight} - ${pageMargin} - ${pageMargin})`;
    const pageStyle = {
        width,
        padding,
        '--rb-page-printable-height': printableHeight,
        ...(isFlowablePage ? { minHeight: height } : { height }),
    };
    const contentClassName = classNames(
        'w-full',
        isFlowablePage ? 'min-h-full overflow-visible' : 'h-full overflow-hidden'
    );

    if (isExport) {
        // Export uses real @page margins. HTML padding is editor-only; Chrome owns
        // the printable content box for table fragments that continue on later pages.
        return (
            <section className={pageClassName} style={pageStyle}>
                <div className={contentClassName}>{children}</div>
            </section>
        );
    }

    return (
        <div ref={containerRef} className="w-full">
            {showFlowableEditorGuide ? <FlowablePageEditorNotice /> : null}
            <div
                className={classNames(
                    'flex justify-center overflow-hidden',
                    isFlowablePage && 'items-start'
                )}
                style={wrapperStyle}
            >
                <section
                    ref={pageRef}
                    className={pageClassName}
                    style={{
                        ...pageStyle,
                        // Preview-only scaling so editor canvas fits viewport.
                        ...transformStyle,
                    }}
                >
                    {showFlowableEditorGuide ? (
                        <FlowablePageBreakGuides pageHeight={pageHeight} pageMargin={pageMargin} />
                    ) : null}
                    {renderSurface === 'editor' ? (
                        <div className="absolute right-2 top-2 z-10 flex flex-col items-end gap-1">
                            <SwarmTypeBadges supportedSwarmTypes={supportedSwarmTypes} />
                        </div>
                    ) : null}
                    <div
                        className={classNames(
                            contentClassName,
                            showFlowableEditorGuide && 'relative z-[2]'
                        )}
                    >
                        {children}
                    </div>
                </section>
            </div>
        </div>
    );
}

export class PageElement extends BaseReportElement<RenderContextKind, PageElementSettings> {
    static readonly elementType = 'page';

    static readonly layout = {
        flow: 'derived',
        getChildDefinitionLists: (settings: ElementSettings) => [
            (settings as PageElementSettings).children,
        ],
    } as const;

    static readonly compatibleContextKinds = ['project', 'measuring-point'] as const;

    static get insertion() {
        return {
            label: gettext('ELEMENT_PAGE_LABEL'),
            supportedSlots: ['template-root'] as const,
            createDefinition: () => ({
                type: 'page',
                settings: { children: [], supportedSwarmTypes: null },
            }),
        };
    }

    static get settingsFormSchema(): ElementSettingsFormSchema {
        return createPageSettingsFormSchema('context-agnostic');
    }

    static resolveSettingsFormSchema({
        contextKind,
    }: ResolveElementSettingsFormSchemaArgs): Promise<ElementSettingsFormSchema> {
        return Promise.resolve(createPageSettingsFormSchema(contextKind));
    }

    protected toCompatibleReactNode(
        ctx: ContextForKind<RenderContextKind>,
        runtime: RenderRuntime
    ): ElementContentRenderResult {
        const childrenPath = runtime.getCurrentElementChildListPath('children');
        const rendered = renderDefinitionListWithAddElementSlot(
            this.settings.children,
            ctx,
            runtime,
            childrenPath,
            ctx.kind,
            'child-list'
        );
        const hasPageContent = rendered.hasRenderableContent;
        const defaultPageProfile = runtime.getPageProfiles()[0];
        if (!defaultPageProfile) {
            throw new Error('Missing default page profile in render runtime.');
        }
        const pageMargin = resolvePageMargin(this.settings, defaultPageProfile.pageMargin);
        const pageSize = this.settings.pageSize ?? defaultPageProfile.pageSize;
        const pageOrientation = this.settings.pageOrientation ?? defaultPageProfile.pageOrientation;
        const pageProfile = runtime.getOrCreatePageProfile({
            pageSize,
            pageOrientation,
            pageMargin,
        });
        const isFlowablePage = runtime.containsFlowableElement(this.settings.children);

        const pageNode = (
            <PageFrame
                pageWidth={pageProfile.width}
                pageHeight={pageProfile.height}
                pageMargin={pageMarginToCss(pageMargin)}
                profileClassName={pageProfile.pageClassName}
                renderSurface={runtime.renderSurface}
                isFlowablePage={isFlowablePage}
                supportedSwarmTypes={this.settings.supportedSwarmTypes}
            >
                {rendered.nodes}
            </PageFrame>
        );
        const visiblePageNode = runtime.showEditor ? (
            pageNode
        ) : (
            <PageContentGate hasContent={hasPageContent}>{pageNode}</PageContentGate>
        );

        return {
            node: visiblePageNode,
            ready: Promise.all([rendered.ready, hasPageContent]).then(() => undefined),
            hasRenderableContent: hasPageContent,
        };
    }

    protected getSupportedSwarmTypes(): SupportedSwarmTypes {
        return this.settings.supportedSwarmTypes;
    }

    protected shouldRenderFrameIncompatibilityNotice(): boolean {
        return false;
    }
}
